import React, { useCallback, useContext, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { JourneyMediasContext } from '../../utils/contexts'
import ExperienceDeleteIcon from '../Quests/ExperienceDeleteIcon'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const DND_TYPE = 'LEARNING_LISTEN_ROW'

const truncateLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

const ListenAudRow = ({
  media,
  index,
  moveCard,
  onDragStart,
  onDragEnd,
  onRequestRemove,
}) => {
  const ref = useRef(null)
  const isModule = media?.type === 'module'

  const [{ handlerId }, drop] = useDrop({
    accept: DND_TYPE,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover(item) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      moveCard(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: DND_TYPE,
    item: () => {
      if (typeof onDragStart === 'function') {
        onDragStart()
      }
      return { id: media._id, index }
    },
    end() {
      if (typeof onDragEnd === 'function') {
        onDragEnd()
      }
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  drag(drop(ref))

  const subtitle =
    media?.subTitle ||
    media?.parentId?.title ||
    media?.categoryId?.title ||
    (isModule ? 'Module' : 'Track')

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={`experience-aud-row${isDragging ? ' aud-dragging' : ''}`}
      style={{ opacity: isDragging ? 0.35 : 1 }}>
      <span className='experience-grab' title='Drag to reorder'>
        ⋮⋮
      </span>
      <span className='experience-qnum'>{index + 1}</span>
      <span className='experience-aud-thumb' aria-hidden='true' />
      <span className='copy'>
        <b>{media?.title || 'Untitled'}</b>
        <small>{subtitle}</small>
      </span>
      <span className='experience-atype'>{isModule ? 'Module' : 'Track'}</span>
      <button
        type='button'
        className='experience-x'
        title='Remove'
        aria-label='Remove'
        onClick={() => onRequestRemove(media)}>
        <ExperienceDeleteIcon />
      </button>
    </div>
  )
}

/**
 * Experience v7 Listen attached pane — numbered aud-rows with Track/Module badge.
 */
const LearningListenAttached = () => {
  const { mediasList, setMediasList, onReorderStart, onReorderEnd } =
    useContext(JourneyMediasContext)
  const [pendingRemove, setPendingRemove] = useState(null)

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      const next = [...mediasList]
      const [moved] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, moved)
      setMediasList(next)
    },
    [mediasList, setMediasList],
  )

  const requestRemove = (media) => {
    setPendingRemove({
      id: media._id,
      label: truncateLabel(media?.title),
    })
  }

  const confirmRemove = () => {
    if (!pendingRemove) return
    const id = pendingRemove.id
    setPendingRemove(null)
    setMediasList(mediasList.filter((m) => m._id !== id))
  }

  const removeMessage = pendingRemove?.label
    ? `\u201C${pendingRemove.label}\u201D will be detached.`
    : 'This item will be detached.'

  return (
    <>
      {mediasList?.length ? (
        <div className='experience-listen-attached'>
          {mediasList.map((media, index) => (
            <ListenAudRow
              key={media._id || media.mediaOrModuleId || index}
              media={media}
              index={index}
              moveCard={moveCard}
              onDragStart={onReorderStart}
              onDragEnd={onReorderEnd}
              onRequestRemove={requestRemove}
            />
          ))}
        </div>
      ) : null}
      <ExperienceConfirmModal
        show={pendingRemove != null}
        onHide={() => setPendingRemove(null)}
        onConfirm={confirmRemove}
        title='Remove this item?'
        message={removeMessage}
        confirmLabel='Delete'
      />
    </>
  )
}

export default LearningListenAttached
